import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AccountBalance as JournalIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../services/apiService';

interface SpecializedVoucherTemplate {
  _id: string;
  name: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  description: string;
  isActive: boolean;
  requiresApproval: boolean;
  hasFees: boolean;
}

const SpecializedVoucherTemplateSelector: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<SpecializedVoucherTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // استخراج مسار العودة من query parameters
  const returnPath = new URLSearchParams(location.search).get('returnPath') || '/finance/specialized-vouchers';

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get<SpecializedVoucherTemplate[]>('/finance/specialized-vouchers/templates');
      
      // تصفية القوالب المفعلة فقط
      const activeTemplates = response.filter(template => template.isActive);
      setTemplates(activeTemplates);
      
    } catch (err: any) {
      console.error('خطأ في تحميل قوالب السندات:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل قوالب السندات');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    navigate(`/finance/specialized-vouchers/new/${templateId}?returnPath=${encodeURIComponent(returnPath)}`);
  };

  const getVoucherTypeIcon = (type: string) => {
    switch (type) {
      case 'receipt': return <ReceiptIcon />;
      case 'payment': return <PaymentIcon />;
      case 'journal': return <JournalIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const getVoucherTypeText = (type: string) => {
    switch (type) {
      case 'receipt': return 'سند قبض';
      case 'payment': return 'سند صرف';
      case 'journal': return 'سند موازنة';
      default: return '';
    }
  };

  const getVoucherTypeColor = (type: string): "success" | "error" | "primary" => {
    switch (type) {
      case 'receipt': return 'success';
      case 'payment': return 'error';
      case 'journal': return 'primary';
      default: return 'primary';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
          اختيار قالب السند المتخصص
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          اختر نوع السند الذي تريد إنشاؤه من القوالب المتاحة أدناه
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {templates.length === 0 ? (
          <Alert severity="info">
            لا توجد قوالب سندات متاحة حالياً
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => handleSelectTemplate(template._id)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {getVoucherTypeIcon(template.voucherType)}
                      <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                        {template.name}
                      </Typography>
                    </Box>
                    
                    <Chip
                      label={getVoucherTypeText(template.voucherType)}
                      color={getVoucherTypeColor(template.voucherType)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    
                    {template.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {template.requiresApproval && (
                        <Chip label="يتطلب موافقة" size="small" variant="outlined" />
                      )}
                      {template.hasFees && (
                        <Chip label="يطبق رسوم" size="small" variant="outlined" />
                      )}
                    </Box>
                  </CardContent>
                  
                  <CardActions>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTemplate(template._id);
                      }}
                    >
                      اختيار هذا القالب
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate(returnPath)}
          >
            العودة
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SpecializedVoucherTemplateSelector;